import React from "react";
import {
  Box,
  Typography,
  Chip,
  Card,
  LinearProgress,
  IconButton,
  Grid
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  EmojiEvents as TrophyIcon,
  WorkspacePremium as MedalIcon,
  MilitaryTech as AwardIcon,
  Person as UserIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as XCircleIcon,
  OpenInNew as ExternalLinkIcon,
  Star as StarIcon
} from "@mui/icons-material";

const RankingView = ({ data }) => {
  const theme = useTheme();
  if (!data || !Array.isArray(data) || data.length === 0) {
    return null;
  }

  const getRankStyle = (index) => {
    switch (index) {
      case 0:
        return {
          bg: 'linear-gradient(to right, #FFFDE7, #FFF8E1)',
          borderColor: '#FBC02D',
          badgeBg: 'linear-gradient(135deg, #FBC02D, #F57F17)',
          icon: <TrophyIcon sx={{ color: 'white', fontSize: 28 }} />,
          rank: "1"
        };
      case 1:
        return {
          bg: 'linear-gradient(to right, #F5F5F5, #FAFAFA)',
          borderColor: '#BDBDBD',
          badgeBg: 'linear-gradient(135deg, #BDBDBD, #757575)',
          icon: <MedalIcon sx={{ color: 'white', fontSize: 28 }} />,
          rank: "2"
        };
      case 2:
        return {
          bg: 'linear-gradient(to right, #FFF3E0, #FFF8E1)',
          borderColor: '#FFB74D',
          badgeBg: 'linear-gradient(135deg, #FFA726, #F57C00)',
          icon: <AwardIcon sx={{ color: 'white', fontSize: 28 }} />,
          rank: "3"
        };
      default:
        return {
          bg: theme.palette.background.paper,
          borderColor: theme.palette.grey[300],
          badgeBg: theme.palette.grey[400],
          icon: <UserIcon sx={{ color: 'white', fontSize: 28 }} />,
          rank: String(index + 1)
        };
    }
  };

  const getRecommendation = (score) => {
    if (score >= 90) return { text: "Excellent candidat", color: "success" };
    if (score >= 75) return { text: "Bon candidat", color: "info" };
    if (score >= 60) return { text: "Candidat acceptable", color: "warning" };
    return { text: "À évaluer", color: "default" };
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrophyIcon sx={{ color: '#FBC02D' }} />
          <Typography variant="h6" fontWeight={700}>
            Classement des candidats
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {data.length} candidat(s)
        </Typography>
      </Box>

      {data.map((item, index) => {
        const style = getRankStyle(index);
        const recommendation = getRecommendation(item.matching_score || 0);
        const skillsArray = Array.isArray(item.matched_skills) 
          ? item.matched_skills 
          : item.matched_skills 
            ? item.matched_skills.split(",").map(s => s.trim()) 
            : [];
        const missingSkillsArray = Array.isArray(item.missing_skills) 
          ? item.missing_skills 
          : item.missing_skills 
            ? item.missing_skills.split(",").map(s => s.trim()) 
            : [];

        return (
          <Card
            key={index}
            elevation={0}
            sx={{
              display: 'flex',
              p: 2.5,
              borderRadius: 4,
              border: '2px solid',
              borderColor: style.borderColor,
              background: style.bg,
              transition: 'box-shadow 0.2s',
              '&:hover': { boxShadow: theme.shadows[4] }
            }}
          >
            {/* Rank Badge */}
            <Box sx={{ mr: 2, flexShrink: 0 }}>
              <Box sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: style.badgeBg,
                boxShadow: theme.shadows[2],
                color: 'white',
                fontWeight: 'bold'
              }}>
                {index < 3 ? style.icon : <Typography variant="h5" fontWeight="bold">{style.rank}</Typography>}
              </Box>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              
              {/* Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" fontWeight={700}>
                      {item.candidate_name || item.name}
                    </Typography>
                    {index === 0 && (
                      <Chip 
                        icon={<StarIcon sx={{ fontSize: '14px !important' }} />}
                        label="Top"
                        size="small"
                        sx={{ bgcolor: '#FFF9C4', color: '#F57F17', fontWeight: 600, '& .MuiChip-icon': { color: '#F57F17' } }}
                      />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {item.job_offer_title || item.position}
                  </Typography>
                </Box>
                <Chip 
                  label={recommendation.text}
                  color={recommendation.color}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Box>

              {/* Score Progress */}
              {item.matching_score && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
                      Score global
                    </Typography>
                    <Typography variant="body2" fontWeight={700} color="success.main">
                      {item.matching_score}%
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={item.matching_score} 
                    color="success"
                    sx={{ height: 10, borderRadius: 5, bgcolor: 'grey.300' }}
                  />
                </Box>
              )}

              {/* Skills */}
              <Grid container spacing={2}>
                {skillsArray.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <CheckCircleIcon color="success" fontSize="small" />
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
                        Points forts ({skillsArray.length})
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {skillsArray.slice(0, 4).map((skill, i) => (
                        <Chip key={i} label={skill} size="small" color="success" sx={{ bgcolor: 'success.50', color: 'success.700', fontWeight: 500 }} />
                      ))}
                      {skillsArray.length > 4 && (
                        <Chip label={`+${skillsArray.length - 4}`} size="small" sx={{ bgcolor: 'success.50', color: 'success.700' }} />
                      )}
                    </Box>
                  </Grid>
                )}

                {missingSkillsArray.length > 0 && (
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <XCircleIcon color="error" fontSize="small" />
                      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
                        Compétences manquantes ({missingSkillsArray.length})
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {missingSkillsArray.slice(0, 4).map((skill, i) => (
                        <Chip key={i} label={skill} size="small" color="error" sx={{ bgcolor: 'error.50', color: 'error.700', fontWeight: 500 }} />
                      ))}
                      {missingSkillsArray.length > 4 && (
                        <Chip label={`+${missingSkillsArray.length - 4}`} size="small" sx={{ bgcolor: 'error.50', color: 'error.700' }} />
                      )}
                    </Box>
                  </Grid>
                )}
              </Grid>
            </Box>

            {/* Action */}
            <Box sx={{ ml: 2, display: 'flex', alignItems: 'flex-start' }}>
              <IconButton color="primary" sx={{ '&:hover': { bgcolor: 'primary.50' } }}>
                <ExternalLinkIcon />
              </IconButton>
            </Box>
          </Card>
        );
      })}
    </Box>
  );
};

export default RankingView;
